import { PARTY_ROLE_DISPLAY, type PartyRoleId, type PartyStatusId } from '@steerfi/shared';
import { IconArrowLeft } from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FileFormatsSection } from '@/components/file-formats-section';
import { IntegrationProfilesSection } from '@/components/integration-profiles-section';
import { DeleteProviderDialog } from '@/components/modals/delete-provider-dialog';
import { EditProviderModal } from '@/components/modals/edit-provider-modal';
import { getStatusColor, getStatusLabel } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/contexts/document-title-context';
import { useProvider } from '@/hooks/use-providers';
import { formatProviderDate } from '@/schemas/provider-form-schema';

export const ProviderDetail: React.FC = () => {
  const { partyId } = useParams<{ partyId: string }>();
  const navigate = useNavigate();
  const { provider, isLoading, error, notFound, refetch } = useProvider(partyId ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useDocumentTitle(provider?.provider_name);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Loading provider…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Provider not found.</p>
        <Link to="/providers" className="text-sm underline underline-offset-4">
          Back to Providers
        </Link>
      </div>
    );
  }

  if (error || !provider || !partyId) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {error ?? 'Failed to load provider.'}
        </div>
        <Link to="/providers" className="text-sm underline underline-offset-4">
          Back to Providers
        </Link>
      </div>
    );
  }

  const statusVariant = getStatusColor(provider.status_id as PartyStatusId, 'party');
  const statusLabel = getStatusLabel(provider.status_id as PartyStatusId, 'party');
  const roleName =
    PARTY_ROLE_DISPLAY[provider.party_role_id as PartyRoleId] ?? String(provider.party_role_id);

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground -ml-2 mb-2"
          onClick={() => navigate('/providers')}
        >
          <IconArrowLeft size={14} />
          Providers
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">{provider.provider_name}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{provider.provider_code}</p>
      </div>

      <div className="px-4 lg:px-6">
        <div className="rounded-md border">
          <div className="flex items-start justify-between p-5 border-b">
            <div>
              <p className="font-medium text-sm">Provider details</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Core attributes for this payment-processing partner.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={() => setDeleteOpen(true)}
              >
                Delete
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 p-5">
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Name
              </p>
              <p className="text-sm">{provider.provider_name}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Code
              </p>
              <p className="text-sm font-mono">{provider.provider_code}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                License #
              </p>
              <p className="text-sm">{provider.license_number ?? '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Role
              </p>
              <p className="text-sm">{roleName}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Status
              </p>
              <Badge
                variant="outline"
                className={`text-xs font-medium ${
                  statusVariant === 'default'
                    ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                    : statusVariant === 'destructive'
                      ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                      : ''
                }`}
              >
                {statusLabel}
              </Badge>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Created
              </p>
              <p className="text-sm">{formatProviderDate(provider.created_at)}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Updated
              </p>
              <p className="text-sm">{formatProviderDate(provider.updated_at)}</p>
            </div>
          </div>
        </div>
      </div>

      <IntegrationProfilesSection partyId={partyId} />
      <FileFormatsSection
        partyId={partyId}
        partyName={provider.provider_name}
        partyCode={provider.provider_code}
      />

      <EditProviderModal
        open={editOpen}
        provider={provider}
        onOpenChange={setEditOpen}
        onSuccess={() => {
          setEditOpen(false);
          refetch();
        }}
      />
      <DeleteProviderDialog
        open={deleteOpen}
        provider={provider}
        onOpenChange={(open) => {
          setDeleteOpen(open);
        }}
        onSuccess={() => navigate('/providers')}
      />
    </div>
  );
};
